/**
 * SRS §8.5: privacy-minimised measurement; no customer identifiers or private URLs.
 * Kenya traffic uses legitimate-interest analytics with a persistent opt-out. Google Consent
 * Mode keeps analytics storage denied by default where its EU User Consent Policy applies.
 */
export type Consent = 'accepted' | 'declined';
export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}
type Params = {
  image_variant?: string;
  contact_method?: 'whatsapp' | 'phone';
  currency?: 'KES';
  value?: number;
  items?: AnalyticsItem[];
  transaction_id?: string;
  shipping?: number;
  payment_type?: string;
  page_location?: string;
  page_title?: string;
  item_list_id?: string;
};
const KEY = 'skytech-analytics-consent';
let initialized = false;
let choice: Consent | null | undefined;
let lastPage = '';
const sent = new Set<string>();
const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
/** EEA member states plus the United Kingdom and Switzerland (Google EU UCP scope). */
const CONSENT_REQUIRED_REGIONS = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'NL',
  'NO',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'CH',
] as const;
export function analyticsConfigured(): boolean {
  return (
    /^G-[A-Z0-9]+$/.test(measurementId) &&
    (import.meta.env.PROD || import.meta.env.VITE_ANALYTICS_DEBUG === 'true')
  );
}
export function getConsent(): Consent | null {
  if (choice !== undefined) return choice;
  try {
    const value = localStorage.getItem(KEY);
    return value === 'accepted' || value === 'declined' ? value : null;
  } catch {
    return null;
  }
}
/** No stored choice means analytics is on under the Kenya-first legitimate-interest policy. */
export function analyticsEnabled(): boolean {
  return getConsent() !== 'declined';
}
function command(...args: unknown[]): void {
  const w = window as Window & { dataLayer?: unknown[] };
  w.dataLayer ??= [];
  // gtag consumes an arguments object, not a nested array.
  // eslint-disable-next-line prefer-rest-params -- Google tag queue requires IArguments.
  w.dataLayer.push(arguments);
  void args;
}
export function setConsent(value: Consent): void {
  (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] =
    value !== 'accepted';
  choice = value;
  lastPage = '';
  try {
    localStorage.setItem(KEY, value);
  } catch {
    /* Session still respects choice. */
  }
  if (value === 'accepted') initializeAnalytics();
  else if (initialized)
    command('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  window.dispatchEvent(new CustomEvent('skytech-consent', { detail: value }));
}
function publicLocation(): string {
  const url = new URL(location.href);
  const path = safePath(url.pathname);
  const params = new URLSearchParams();
  if (path !== '/checkout') {
    for (const key of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_id',
      'gclid',
      'gbraid',
      'wbraid',
    ]) {
      const value = url.searchParams.get(key);
      if (value && /^[a-zA-Z0-9_.~-]{1,200}$/.test(value)) params.set(key, value);
    }
  }
  return `${url.origin}${path}${params.size ? `?${params}` : ''}`;
}
function safeReferrer(): string {
  try {
    return new URL(document.referrer).origin;
  } catch {
    return '';
  }
}
export function initializeAnalytics(): void {
  if (!analyticsConfigured() || !analyticsEnabled()) return;
  if (initialized) {
    // Only an explicit choice may override Google's denied regional default.
    if (getConsent() === 'accepted')
      command('consent', 'update', { analytics_storage: 'granted' });
    return;
  }
  (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = false;
  initialized = true;
  command('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  command('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    region: CONSENT_REQUIRED_REGIONS,
  });
  if (getConsent() === 'accepted')
    command('consent', 'update', { analytics_storage: 'granted' });
  command('js', new Date());
  command('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_referrer: safeReferrer(),
    page_location: publicLocation(),
    page_title: 'SkyTech Solutions',
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.id = 'skytech-google-tag';
  document.head.append(script);
}
export function track(event: string, params: Params = {}, once?: string): void {
  if (!analyticsConfigured() || !analyticsEnabled()) return;
  initializeAnalytics();
  if (once && sent.has(once)) return;
  if (once) sent.add(once);
  command('event', event, {
    page_location: publicLocation(),
    page_referrer: safeReferrer(),
    page_title: 'SkyTech Solutions',
    ...params,
  });
}
function safePath(path: string): string {
  return path === '/' || path === '/products' || /^\/(products|category)\/[a-z0-9-]+$/.test(path)
    ? path
    : '/checkout';
}
export function pageView(path: string, title: string): void {
  // Private order IDs, auth/reset parameters, searches and contact details never leave the site.
  const safe =
    path === '/' || path === '/products' || /^\/(products|category)\/[a-z0-9-]+$/.test(path);
  if (!safe || !analyticsEnabled() || path === lastPage) return;
  lastPage = path;
  track('page_view', { page_location: publicLocation(), page_title: title });
}
export function item(
  product: { id: string; name: string; price: string },
  quantity = 1,
): AnalyticsItem {
  return { item_id: product.id, item_name: product.name, price: Number(product.price), quantity };
}
